import { Feature, Point } from "geojson";
import { useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { MaptilerFeature, useMaptilerGeosearch } from "@nens/react/hooks";

import { useFormFieldError } from "../../hooks/use-rana-form-field-error";
import { RanaFormContext } from "../../rana-form";
import { convertValueToString } from "../rana-form-helpers/helpers";

export function useRanaFormGeocoderInput(field: string, maptilerApiKey: string) {
  const intl = useIntl();
  const locale = intl.locale ?? "en";

  const [searchString, actualSetSearchString] = useState("");
  const [disableGeocoder, setDisableGeocoder] = useState(false);

  const setSearchString = (s: string) => {
    setDisableGeocoder(false);
    actualSetSearchString(s);
  };

  const { setPartialValues, values, geoProps } = useContext(RanaFormContext);
  const { bbox, filterGeoByMapBbox } = geoProps;

  const error = useFormFieldError(field);
  const value = (values[field] as unknown as Feature<Point>) ?? null;

  useEffect(() => {
    if (value) {
      setDisableGeocoder(true);
      actualSetSearchString("");
    }
  }, [value]);

  const geocoderResponse = useMaptilerGeosearch({
    maptilerApiKey,
    searchString,
    locale,
    bbox: filterGeoByMapBbox ? bbox : [-180, -90, 180, 90],
    enabled: !!(!disableGeocoder && searchString && maptilerApiKey),
  });

  const setMaptilerFeature = (feature?: MaptilerFeature) => {
    if (feature) {
      const geoJsonFeature: Feature<Point> = {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: feature.center,
        },
        properties: {
          name: feature.place_name,
        },
      };
      setPartialValues({ [field]: geoJsonFeature });
    } else {
      setPartialValues({ [field]: null });
    }
  };

  const geocoderFeatures: MaptilerFeature[] = geocoderResponse.data?.features ?? [];

  return {
    geocoderFeatures,
    searchString,
    setSearchString,
    setMaptilerFeature,
    error,
    value: convertValueToString(value),
  };
}
